import { useTranslation } from 'react-i18next';
import { cn } from '@/shared/lib/utils';
import { PermissionAccessLevel } from '@/api/generated/schemas/permissionAccessLevel';

const LEVELS = [
  PermissionAccessLevel.None,
  PermissionAccessLevel.Read,
  PermissionAccessLevel.Edit,
] as const;

const LEVEL_LABEL_KEY: Record<PermissionAccessLevel, string> = {
  None: 'level.none',
  Read: 'level.read',
  Edit: 'level.edit',
};

// Sémantické tóny místo holé Tailwind palety: `blue-*`/`green-*` neposlechly žádné
// z 18 schémat ani 12 feelů, takže přepínač byl v jiném motivu vizuálně cizí. Tinty se
// navíc přepínají podle data-phx-mode samy, takže ruční dark: páry odpadají — text
// přechází na mode-aware základ stejně jako u Badge (soft varianta).
const ACTIVE_CLASS: Record<PermissionAccessLevel, string> = {
  None: 'bg-phx-surface-2 text-phx-text border-phx-border-2',
  Read: 'bg-phx-info-tint text-phx-info-text border-phx-info dark:text-phx-info',
  Edit: 'bg-phx-success-tint text-phx-success-text border-phx-success dark:text-phx-success',
};

interface PermissionLevelControlProps {
  /** Explicitní grant uzlu (`None` = bez grantu). */
  value: PermissionAccessLevel;
  disabled?: boolean;
  onChange: (level: PermissionAccessLevel) => void;
}

/** Tri-state přepínač úrovně grantu (Žádné / Čtení / Editace) — ukládá se hned při změně. */
export function PermissionLevelControl({ value, disabled, onChange }: PermissionLevelControlProps) {
  const { t } = useTranslation('security');

  return (
    <div className="border-phx-border-2 rounded-phx inline-flex shrink-0 border" role="group">
      {LEVELS.map((level, i) => {
        const isActive = value === level;
        return (
          <button
            key={level}
            type="button"
            disabled={disabled || isActive}
            aria-pressed={isActive}
            className={cn(
              'px-phx-3 py-phx-1 text-phx-xs cursor-pointer transition-colors',
              i > 0 && 'border-phx-border-2 border-l',
              i === 0 && 'rounded-l-phx',
              i === LEVELS.length - 1 && 'rounded-r-phx',
              isActive
                ? ACTIVE_CLASS[level]
                : 'bg-phx-surface text-phx-text-muted hover:bg-phx-surface-2 hover:text-phx-text',
              disabled && 'cursor-not-allowed opacity-60'
            )}
            onClick={() => onChange(level)}
          >
            {t(LEVEL_LABEL_KEY[level])}
          </button>
        );
      })}
    </div>
  );
}
